import { useEffect } from 'react';
import { dispatchSyntheticBrowserUndoRedoEvent } from '../../utils/undoRedo/undoRedoBrowserUtils';
import { Clipboard } from '@capacitor/clipboard';
import { EditingAction } from '../../../capacitor_plugins/editing';
import { EditActionData } from '../../../capacitor_plugins/editing/definitions';
import { useDispatch } from 'react-redux';
import { getClipboardLastEditedTimestamp } from '../../workspace/shortcuts/clipboard/clipboardSelectors';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { CAPACITOR_EDIT_ACTION_PLUGIN_NAME } from '../../../capacitor_plugins/pluginConstants';
import { pasteElements } from '../../workspace/shortcuts/clipboard/clipboardActions';
import { createElementAsync } from '../../element/actions/elementActions';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { ElementType } from '../../../common/elements/elementTypes';
import { BoardSections } from '../../../common/boards/boardConstants';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { isImageMimeType } from '../../../common/media/mimeTypeUtils';
import { dataUriToFile } from '../../../common/utils/dataUriUtils';

enum EditAction {
    COPY = 'copy',
    CUT = 'cut',
    PASTE = 'paste',
    UNDO = 'undo',
    REDO = 'redo',
}

// @ts-ignore - Paste elements is already being called in JS with an empty object.
const emptyPasteElementsAction = () => pasteElements({});

/**
 * Fires a synthetic 'paste' event with the given type and value, attempting
 * to get the browser to handle the pasted data using its default behavior.
 */
const dispatchSyntheticBrowserPasteEvent = (type: string, value: string) => {
    const syntheticPaste = new ClipboardEvent('paste', {
        clipboardData: new DataTransfer(),
    });
    syntheticPaste.clipboardData?.setData(type, value);
    document.dispatchEvent(syntheticPaste);
};

const createLocationForPastedElement = (boardId: string) => ({
    parentId: boardId,
    section: BoardSections.INBOX,
    position: { index: 0 },
});

const handlePaste = (data: EditActionData) => async (dispatch: Function, getState: Function) => {
    const state = getState();

    const currentBoardId = getCurrentBoardId(state);

    const webClipboardLastEditedTimestampMilliseconds = getClipboardLastEditedTimestamp(state);
    const webClipboardLastEditedTimestamp: Date | undefined = webClipboardLastEditedTimestampMilliseconds
        ? new Date(webClipboardLastEditedTimestampMilliseconds)
        : undefined;

    const nativeClipboardLastEditedTimestamp = new Date(data.lastChangedTimestamp);

    const webClipboardHasLatestChange =
        webClipboardLastEditedTimestamp &&
        nativeClipboardLastEditedTimestamp.getTime() < webClipboardLastEditedTimestamp.getTime();

    // Web clipboard has the latest change, so we should attempt to paste from web clipboard.
    if (webClipboardHasLatestChange) return dispatch(emptyPasteElementsAction());

    // Web clipboard has not yet been edited (webClipboardLastEditedTimestamp === undefined), OR
    // Native clipboard has the latest change, so we should attempt to paste from the native clipboard.
    try {
        // If Capacitor's Clipboard is unable to find data on the native clipboard, or the data on
        // the native clipboard is NOT supported, it throws an error here, and we fall back onto the
        // web clipboard.
        // Example of unsupported data: PDF copied from Files.app on iOS.
        const { type, value } = await Clipboard.read();

        // If the data isn't an image perform Paste as 'normal'.
        if (!isImageMimeType(type)) return dispatchSyntheticBrowserPasteEvent(type, value);

        // If the data is some sort of image, 'value' is a Data URL representation of the image.
        // Dispatch a Redux action to signal our app to create an element and perform
        // necessary async side-effects.
        const file = await dataUriToFile(value, 'image');

        // If unable to parse into a 'File' object, fall back to creating whatever we can.
        // At least this way, we'll receive messages in support about weird blocks of text
        // being created instead of a user's copied image and they still 'have' their image.
        if (!file) return dispatchSyntheticBrowserPasteEvent(type, value);

        const transactionId = getNewTransactionId();

        dispatch(
            createElementAsync({
                attachment: file,
                elementType: ElementType.IMAGE_TYPE,
                location: createLocationForPastedElement(currentBoardId),
                currentBoardId: currentBoardId,
                transactionId,
            }),
        );
    } catch {
        // Fall back onto using the web clipboard.
        dispatch(emptyPasteElementsAction());
    }
};

export default function () {
    const dispatch = useDispatch();

    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_EDIT_ACTION_PLUGIN_NAME)) return;

        let editingListenerAction: PluginListenerHandle | null = null;

        EditingAction.addListener('editAction', async (data: EditActionData) => {
            switch (data.action) {
                case EditAction.COPY:
                    return document.dispatchEvent(new ClipboardEvent('copy', { clipboardData: new DataTransfer() }));
                case EditAction.CUT:
                    return document.dispatchEvent(new ClipboardEvent('cut', { clipboardData: new DataTransfer() }));
                case EditAction.PASTE:
                    return dispatch(handlePaste(data));
                case EditAction.UNDO:
                    return dispatchSyntheticBrowserUndoRedoEvent({ shiftKey: false });
                case EditAction.REDO:
                    return dispatchSyntheticBrowserUndoRedoEvent({ shiftKey: true });
                default:
                    return;
            }
        }).then((listener) => {
            editingListenerAction = listener;
        });

        return () => {
            editingListenerAction?.remove();
        };
    }, []);
}
